import BaseChart from './base-chart';
import { service } from '@ember/service';
import { computed } from '@ember/object';
import Utils from '../../utils/utils';
import Constants from '../../utils/constants';

export default class InputSettlingChart extends BaseChart {
    @service tabService;
    @service pdfExport;
    @service('data') model;
    @service errorHandling;
    @service intl;
    @service plotData;
    @service componentManager;
    @service sigmaDeltaData;

    tagName = '';

    init() {
        super.init();
        this.set('pdfExport.tabs.' + this.chartId, this);
    }

    didRender() {
        super.didRender();
        if (!this.justZoomed) this.draw();
        else this.set('justZoomed', false);
    }

    @computed('chartId') get chartType() {
        return this.chartId.replace('Chart', '');
    }

    _setPlotData() {
        let plotData = this.plotData.getData(this.chartType, this.model.inputSettlingChart);
        this.setProperties(plotData);
    }

    _roundTickValueToRange(value, step, range) {
        return step === 0
            ? Utils.roundTo(value / range.value, 0) * range.value + range.partial
            : Utils.roundToStep(value, step);
    }

    _getTickInterval(tickStep, yDiff) {
        let initialValue = (yDiff * this.padY) / this.numPlotTicks,
            numericRange = Utils.getNumericRange(initialValue, true),
            tickInterval = this._roundTickValueToRange(initialValue, tickStep, numericRange);

        return tickInterval < tickStep ? tickStep : tickInterval;
    }

    _getInitialTick(minY, maxY) {
        let yDiff = maxY - minY,
            tickStep = this.tickStep,
            tickInterval = this._getTickInterval(tickStep, yDiff),
            intervalNumericRange = Utils.getNumericRange(tickInterval),
            initialValue = maxY + tickInterval * this.ticksAbove;

        return {
            value: this._roundTickValueToRange(initialValue, tickStep, intervalNumericRange),
            interval: tickInterval,
        };
    }

    _generateTicks(minY, maxY) {
        this.ticks = [];

        let totalPlotTicks = this.numPlotTicks + this.ticksAbove + this.ticksBelow,
            i = totalPlotTicks - 1,
            tickData = this._getInitialTick(minY, maxY);

        this.ticks[i] = tickData.value;
        this.tickInterval = tickData.interval;

        while (i > 0) {
            i -= 1;
            this.ticks[i] = this.ticks[i + 1] - this.tickInterval;
        }
    }

    draw(force = false) {
        if (window.$('#' + this.chartId)) {
            window.$('#' + this.chartId).empty();
        }

        if (this.isDestroyed) {
            return;
        }

        this._setPlotData();

        if (!force && (this.errorHandling.tabError || this.errorHandling.vSupplyError)) {
            this.set('zoomed', false);
            return;
        }

        let chartElement = document.querySelector(`#${this.chartId}`);

        // Make sure the chart container is there and has width before we initialize the chart with jqplot
        if (!(force || (chartElement && chartElement.offsetWidth))) return;

        let selfRef = this,
            series = [],
            seriesColors = [],
            legendLabels = [],
            plotLines = this.plotLines,
            x = this.x;

        let start = x[0],
            end = x[x.length - 1];

        let lines = [],
            // ticks = [],
            yMaxes = [],
            yMins = [];

        for (let i = 0; i < plotLines.length; i++) {
            let plotLine = plotLines[i],
                s = {};

            if (plotLines[i].hidden) continue;

            if (plotLine.markers) {
                s = {
                    showLine: false,
                    showMarker: true,
                    markerRenderer: window.jQuery.jqplot.markerRenderer,
                    markerOptions: {
                        show: true,
                        style: 'filledDiamond',
                        size: 14,
                        lineWidth: 2,
                    },
                };
            } else if (plotLine.dashed) s.linePattern = 'dashed';
            else s.lineWidth = 3;

            series.push(s);
            seriesColors.push(plotLine.color);
            if (!plotLine.hideLegend) legendLabels.push(plotLine.name.toString());

            // Builds up the lines here
            let _y,
                _x,
                line = [];

            if (plotLine.arrayWithX) {
                let points = plotLine.arrayWithX,
                    jLen = points.length;

                _y = [];
                _x = [];

                for (let j = 0; j < jLen; j++) {
                    _x.push(points[j][0]);
                    _y.push(points[j][1]);
                }
            } else {
                _y = plotLine.array;
                _x = x;
            }

            let _min = _y[0],
                _max = _y[0],
                loopLen = _x.length;

            for (let j = 0; j < loopLen; j++) {
                if (_y[j] !== false) {
                    // Get the current lines max and min y values.
                    let a = _y[j],
                        currentMin = a,
                        currentMax = a;

                    if (_min > currentMin) _min = currentMin;
                    if (_max < currentMax) _max = currentMax;

                    // Add value to line.
                    line.push([_x[j], _y[j]]);
                }
            }

            yMaxes.push(_max);
            yMins.push(_min);
            lines.push(line);
        }

        let minY = Math.min.apply(null, yMins),
            maxY = Math.max.apply(null, yMaxes);

        this._generateTicks(minY, maxY);

        // #region Markers for conversion and acquisition.
        // Code to get the cycle labels on the chart...
        // TODO: Not Dynamic. Put this in a child....
        // var labelLocationY = this.cycleLabelTop ? (maxY + ((maxY - minY) * 0.05)) : (minY - ((maxY - minY) * 0.05));
        let labelLocationY = this.cycleLabelTop ? maxY : minY - (maxY - minY) * 0.04;
        labelLocationY -= this.tickInterval / 4;

        // var percentToReduce = totalWidth * 0.008;
        let percentToReduce = 0;

        // Conversion cycle marker line.
        let cycleStart = this.cycle1End + percentToReduce;
        let cycleEnd = this.timeAtKickback - percentToReduce;
        let centerX = (cycleStart + cycleEnd) / 2;

        if (!this.sigmaDeltaData.isSigmaDeltaAdc) {
            lines.push([
                [cycleStart, labelLocationY, '|'],
                [centerX, labelLocationY, selfRef.intl.t('chart_labels.conversion_cycle')],
                [cycleEnd, labelLocationY, '|'],
            ]);
        } else {
            lines.push([
                [cycleStart, labelLocationY, '|'],
                [centerX, labelLocationY, ''],
                [cycleEnd, labelLocationY, '|'],
            ]);
        }

        // Acquisition cycle marker line.
        cycleStart = this.timeAtKickback + percentToReduce;
        cycleEnd = this.cycle3Start - percentToReduce;
        centerX = (cycleStart + cycleEnd) / 2;

        if (!this.sigmaDeltaData.isSigmaDeltaAdc) {
            lines.push([
                [cycleStart, labelLocationY, '|'],
                [centerX, labelLocationY, selfRef.intl.t('chart_labels.acquisition_cycle')],
                [cycleEnd, labelLocationY, '|'],
            ]);
        } else {
            lines.push([
                [cycleStart, labelLocationY, '|'],
                [centerX, labelLocationY, ''],
                [cycleEnd, labelLocationY, '|'],
            ]);
        }

        let labelSeries = {
            showLine: true,
            showHighlight: false,
            lineWidth: 4,
            pointLabels: {
                show: true,
                ypadding: -20,
                formatString: ' ', // This has to be set to something truthy
            },
        };

        series.push(labelSeries, labelSeries);
        seriesColors.push('#333', '#333');
        // #endregion Markers for conversion and acquisition.

        let cursor;

        if (this.zoomEnabled) {
            cursor = {
                tooltipLocation: this.tooltipLocation,
                show: true,
                zoom: true,
                style: `url(images/cursor_zoom.cur), default`,
            };
        } else {
            cursor = {
                tooltipLocation: this.tooltipLocation,
                show: true,
                showTooltip: true,
            };
        }

        // TODO: Not Dynamic. Put this in a child....
        let canvasOverlay = {};
        if (typeof this.cycle1End !== 'undefined' && typeof this.cycle3Start !== 'undefined') {
            canvasOverlay = {
                show: true,
                objects: [
                    {
                        rectangle: {
                            xmin: start,
                            xmax: selfRef.cycle1End,
                            xminOffset: '0px',
                            xmaxOffset: '0px',
                            yminOffset: '0px',
                            ymaxOffset: '0px',
                            color: 'rgba(0, 0, 0, 0.1)',
                            shadow: false,
                        },
                    },

                    {
                        rectangle: {
                            xmin: selfRef.cycle3Start,
                            xmax: end,
                            xminOffset: '0px',
                            xmaxOffset: '0px',
                            yminOffset: '0px',
                            ymaxOffset: '0px',
                            color: 'rgba(0, 0, 0, 0.1)',
                            shadow: false,
                        },
                    },
                ],
            };
        }

        let plot = window.jQuery.jqplot(this.chartId, lines, {
            height: 450,
            canvasOverlay: canvasOverlay,
            axesDefaults: {
                labelRenderer: window.jQuery.jqplot.CanvasAxisLabelRenderer,
            },
            seriesDefaults: {
                showMarker: false,
                shadow: false,
                // Note: This can show false points, but it usually makes the line more appealing.
                rendererOptions: {
                    smooth: false,
                },
            },
            highlighter: {
                show: true,
                useAxesFormatters: true,
            },
            series: series,
            axes: {
                xaxis: {
                    label: selfRef.xLabel,
                    tickOptions: {
                        formatter: selfRef.roundToNsPrettyTickFormatter,
                    },
                    min: start,
                    max: end,
                    pad: 0,
                    numberTicks: 8,
                },
                yaxis: {
                    label: selfRef.yLabel,
                    tickOptions: {
                        formatter: selfRef.prettyTickFormatter,
                    },
                    min: selfRef.minY,
                    max: selfRef.maxY,

                    pad: 1.65,

                    // the ticks can't be specified if we want to use jqplot's zoom
                    //ticks: selfRef.ticks,
                },
            },
            cursor: cursor,
            grid: {
                borderColor: '#E5E5E5',
                shadow: false,
                background: '#FFFFFF',
            },
            legend: {
                show: this.showLegend,
                location: 'e', //selfRef.legendLocation,
                placement: 'insideGrid',
                labels: legendLabels,
            },
            seriesColors: seriesColors,
        });

        if (this.zoomEnabled) this.enableZoom(plot);

        this.trimLegend();

        let eventCanvasElement = document.querySelector(`#${this.chartId} .jqplot-event-canvas`);
        if (eventCanvasElement)
            eventCanvasElement.setAttribute(
                Constants.QA_ATTR_NAME,
                `${Constants.QA_ATTR_PREFIX}_${this.chartType}_graph`,
            );

        let resizeTimeout;
        const onResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                if (plot && chartElement && chartElement.offsetWidth > 0) {
                    plot.replot({ resetAxes: false }); // Re-draw the plot
                    this.trimLegend();
                }
            }, 100);
        };
        window.addEventListener('resize', onResize);
    }
}
